import { z } from "zod";
import { addDays, dayNo, isValidYmd, scheduleFill, weekdayOf, type ScheduleRow } from "./schedule.js";

// ---------------------------------------------------------------------------
// 工程表の出力（工程管理の5回目、#104、docs/39 §7。Figma F1・F2）
//   形式は Excel（見た目のまま）・PDF・CSV。用紙は A3 横（既定）・A4 横・A4 縦。見るだけのときも出せる
//   紙の形と中身は packages/paper の schedulePaperModel・renderSchedulePaper（ダイアログの見本と PDF で同じもの）。
//   Excel は apps/api が exceljs で同じ形から作る。CSV は今の toCsv（BOM つき・CRLF）
//   ここは 画面と API が同じものを使う決まり（上限・入れるもの・期間・ファイルの名前・画面の文）
// ---------------------------------------------------------------------------

/** 上限（docs/39 §7.4）。期間 400日・紙 30枚・行 300・1人 1分に 10回 */
export const SCHEDULE_EXPORT_LIMITS = { days: 400, pages: 30, rows: 300, perMinute: 10 } as const;

export const ScheduleExportFormat = z.enum(["xlsx", "pdf", "csv"]);
export type ScheduleExportFormat = z.infer<typeof ScheduleExportFormat>;

export const SchedulePaperKey = z.enum(["a3l", "a4l", "a4p"]);
export type SchedulePaperKey = z.infer<typeof SchedulePaperKey>;
/** 用紙（mm）。excel は Excel の用紙の番号（A3＝8・A4＝9） */
export const SCHEDULE_PAPERS: Record<SchedulePaperKey, { w: number; h: number; label: string; landscape: boolean; excel: number }> = {
  a3l: { w: 420, h: 297, label: "A3 横", landscape: true, excel: 8 },
  a4l: { w: 297, h: 210, label: "A4 横", landscape: true, excel: 9 },
  a4p: { w: 210, h: 297, label: "A4 縦", landscape: false, excel: 9 },
};

/**
 * 目盛（#290、docs/102 §2.1。#291 で週をやめて旬＝§2.1.1）：自動（既定）／日／旬／月。PDF と見本だけ（Excel は日の列のまま・CSV は変えない）。
 *   自動＝日（日数 × 2.5mm が入る）→ 旬（1〜10日・11〜20日・21〜末日。1旬 6mm 以上）→ 月（1か月 12mm 以上）→ どれも入らなければ月で期間を紙に分ける。
 *   問い合わせで省いたときは「日」（#290 の前に作った URL は前と同じ紙）。画面のダイアログは「自動」から始める。
 *   前の値 week（#290 の週）は旬として受ける（覚えた設定・前の URL の互換）
 */
export const SCHEDULE_SCALES = ["auto", "day", "jun", "month"] as const;
export type ScheduleScale = (typeof SCHEDULE_SCALES)[number];
/** 目盛の値を読む（前の week は旬）。知らない値は null */
export const scheduleScaleOf = (v: unknown): ScheduleScale | null => (v === "week" ? "jun" : (SCHEDULE_SCALES as readonly unknown[]).includes(v) ? (v as ScheduleScale) : null);
export const ScheduleScale = z.preprocess((v) => (v === "week" ? "jun" : v), z.enum(SCHEDULE_SCALES));
export const SCHEDULE_SCALE_LABEL: Record<ScheduleScale, string> = { auto: "自動", day: "日", jun: "旬", month: "月" };
/** 旬の言葉（#291）：見出し（狭ければ1字）・ボタンの title */
export const SCHEDULE_JUN_LABEL = ["上旬", "中旬", "下旬"] as const;
export const SCHEDULE_JUN_SHORT = ["上", "中", "下"] as const;
export const SCHEDULE_JUN_TITLE = "上旬・中旬・下旬";

/** 入れるもの（docs/39 §7.2 の6。#290 で 開始・日数・#291 で 概略・備考・バーの日付）。金額（社内用）とバーの日付はブラウザに覚えない */
export const SCHEDULE_INCLUDE_KEYS = ["addressee", "assignee", "start", "days", "progress", "crew", "today", "memo", "amount", "summary", "note", "barDates"] as const;
export type ScheduleIncludeKey = (typeof SCHEDULE_INCLUDE_KEYS)[number];
export type ScheduleInclude = Record<ScheduleIncludeKey, boolean>;
/** 全体の工程表から出すときに選べるもの（宛名・担当・進捗・メモ・金額は無い。開始・日数は{siteShort}別だけの列） */
export const OVERVIEW_INCLUDE_KEYS = ["start", "days", "crew", "today", "barDates"] as const satisfies readonly ScheduleIncludeKey[];
/**
 * 既定。概略（summary＝見出しの行だけ・中の{task}を名前つきで並べる。#291）は既定 OFF、備考（note＝見出しの説明の列。概略のときだけ効く）は既定 ON。
 * 概略を ON にしたとき 開始・日数を外すのは画面（出力の窓）の既定（手で ON にできる）。
 * バーの日付（barDates＝バーの両端の「10/13」「11/14」。#291 §2.1.1）は問い合わせで省いたとき OFF（前の URL と同じ紙）。
 * 出力の窓の既定は目盛で決める（旬・月は ON・日は OFF＝barDatesDefault）
 */
export const SCHEDULE_INCLUDE_DEFAULT: ScheduleInclude = { addressee: true, assignee: true, start: true, days: true, progress: true, crew: true, today: true, memo: true, amount: false, summary: false, note: true, barDates: false };
/** 出力の窓のバーの日付の既定（決まった目盛で。旬・月は ON、日は OFF） */
export const barDatesDefault = (scale: "day" | "jun" | "month"): boolean => scale !== "day";
export const SCHEDULE_INCLUDE_LABEL: Record<ScheduleIncludeKey, string> = {
  addressee: "宛名",
  assignee: "担当",
  start: "開始",
  days: "日数",
  progress: "進捗",
  crew: "人数",
  today: "今日の線",
  memo: "メモの行",
  amount: "金額（社内用）",
  summary: "概略",
  note: "備考",
  barDates: "バーの日付",
};
/** 入れるものの名前（#292：％ OFF の会社は「進捗」→「状況」） */
export const includeLabelOf = (k: ScheduleIncludeKey, pct: boolean): string => (k === "progress" && !pct ? "状況" : SCHEDULE_INCLUDE_LABEL[k]);

/**
 * include=addressee,assignee,… を読む。省いたら既定（金額は外す）。空の文字なら「どれも入れない」。
 * allowed の外（全体の工程表の addressee など）と知らない言葉は無視する
 */
export function parseInclude(raw: string | undefined | null, allowed: readonly ScheduleIncludeKey[] = SCHEDULE_INCLUDE_KEYS): ScheduleInclude {
  const out: ScheduleInclude = { addressee: false, assignee: false, start: false, days: false, progress: false, crew: false, today: false, memo: false, amount: false, summary: false, note: false, barDates: false };
  if (raw == null) {
    for (const k of allowed) out[k] = SCHEDULE_INCLUDE_DEFAULT[k];
    return out;
  }
  const want = new Set(raw.split(",").map((s) => s.trim()));
  for (const k of allowed) out[k] = want.has(k);
  return out;
}
/** 入れるもの → include=…（並びは決まった順） */
export const includeParam = (inc: ScheduleInclude, allowed: readonly ScheduleIncludeKey[] = SCHEDULE_INCLUDE_KEYS): string => allowed.filter((k) => inc[k]).join(",");

/* ---------------- 期間 ---------------- */

/** 期間の選び方：全体（最初の工程〜最後の工程か節目）・今月・今週・日付で選ぶ／全体の工程表は「見ている期間」 */
export type SchedulePeriodKind = "all" | "month" | "week" | "custom" | "view";
/** 今月（1日〜末日） */
export function monthRangeOf(today: string): { from: string; to: string } {
  const from = `${today.slice(0, 7)}-01`;
  const next = addDays(from, 32);
  return { from, to: addDays(`${next.slice(0, 7)}-01`, -1) };
}
/** 今週（月曜〜日曜） */
export function weekRangeOf(today: string): { from: string; to: string } {
  const from = addDays(today, -((weekdayOf(today) + 6) % 7));
  return { from, to: addDays(from, 6) };
}
/** 工程表の全体（日付のある工程と節目の最初〜最後）。日付のある行が無ければ null */
export function scheduleSpanOf(rows: readonly Pick<ScheduleRow, "kind" | "start" | "end">[]): { from: string; to: string } | null {
  let from: string | null = null;
  let to: string | null = null;
  for (const r of rows) {
    // 工程・節目と、手で期間を入れた見出し（#291）
    if (r.kind === "memo" || !r.start) continue;
    const e = r.end ?? r.start;
    if (!from || r.start < from) from = r.start;
    if (!to || e > to) to = e;
  }
  return from && to ? { from, to } : null;
}
/** 期間の日数（両端を含む） */
export const periodDays = (from: string, to: string): number => dayNo(to) - dayNo(from) + 1;
/** 期間の検査（形・向き・400日）。よければ null、だめなら画面の文 */
export function periodProblem(from: string, to: string): string | null {
  if (!isValidYmd(from) || !isValidYmd(to)) return SCHEDULE_EXPORT_MSG.periodShape;
  if (to < from) return SCHEDULE_EXPORT_MSG.periodOrder;
  if (periodDays(from, to) > SCHEDULE_EXPORT_LIMITS.days) return SCHEDULE_EXPORT_MSG.periodTooLong;
  return null;
}

/* ---------------- ファイルの名前（docs/39 §7.2 の11） ---------------- */

/** ファイル名に使えない文字（\ / : * ? " < > | と制御文字）は「_」。前後の空白と「.」は落とす。長すぎる名前は切る。空なら fallback（既定「工程表」） */
export function safeFileName(s: string, fallback = "工程表"): string {
  const t = s
    .replace(/[\\/:*?"<>|\u0000-\u001f\u007f]/g, "_")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\.+|\.+$/g, "");
  return (t || fallback).slice(0, 80);
}
/**
 * 「工程表_{案件名}_{YYYYMMDD}.xlsx」「工程表_協力会社別_{YYYYMMDD}.pdf」。
 * 「工程表」「協力会社別」「現場別」は会社の言葉（#142 段5 No.87）：fill は scheduleFill（画面は t）。省けば現場力の既定の言葉
 */
export function scheduleExportFileName(what: { group: string } | { overview: "site" | "vendor" }, today: string, format: ScheduleExportFormat, fill: (text: string) => string = scheduleFill()): string {
  const head = safeFileName(fill("{schedule}"));
  const name = "group" in what ? safeFileName(what.group, head) : safeFileName(fill(what.overview === "vendor" ? "{contractor}別" : "{siteShort}別"));
  return `${head}_${name}_${today.replace(/-/g, "")}.${format}`;
}

/* ---------------- 画面と紙の文（1か所） ---------------- */

export const SCHEDULE_EXPORT_MSG = {
  button: "出力",
  title: "{schedule}出力",
  empty: "{task}がありません",
  format: "形式",
  formats: {
    xlsx: { name: "Excel（見た目のまま）", sub: ".xlsx・色・線・休日の色もそのまま" },
    pdf: { name: "PDF", sub: "印刷・メール送信" },
    csv: { name: "CSV", sub: "データのみ（他のソフト用）" },
  } as Record<ScheduleExportFormat, { name: string; sub: string }>,
  excelNote: "Excel は見た目の写しです。Excel で修正しても{schedule}に反映しません",
  csvNote: "データのみ（見た目なし）",
  paper: "用紙",
  scale: "目盛",
  period: "期間",
  periods: { all: "全体", month: "今月", week: "今週", custom: "日付指定", view: "表示中の期間" } as Record<SchedulePeriodKind, string>,
  from: "から",
  to: "まで",
  include: "印字項目",
  /** 「{task}の列を広く」（#300）：1列目の見出しの言葉（{task}・{trade}・{siteShort}・{task}・{contractor}・担当）＋「の列を広く」 */
  wideName: (col: string) => `${col}の列を広く`,
  cancel: "取消",
  submit: "出力",
  making: "作成中…",
  sheets: (paper: string, n: number) => `${paper}・${n}枚`,
  preview: "見本（1枚目）",
  previewOpen: "見本拡大",
  previewClose: "閉じる",
  done: (name: string) => `「${name}」を出力しました`,
  failed: "出力できませんでした",
  periodShape: "日付の形（YYYY-MM-DD）になっていません",
  periodOrder: "「まで」は「から」より後の日にしてください",
  periodTooLong: `期間は${SCHEDULE_EXPORT_LIMITS.days}日までです`,
  tooManyPages: (n: number) => `用紙が${n}枚になります。${SCHEDULE_EXPORT_LIMITS.pages}枚までです。期間を短くしてください`,
  tooManyRows: (n: number, overview = false) => `行が${n}行あります。${SCHEDULE_EXPORT_LIMITS.rows}行までです${overview ? "。期間を短くするか、検索で絞り込んでください" : ""}`,
  overviewTooMany: "{siteShort}が多すぎます。期間を短くするか、検索で絞り込んでください",
  /* 紙の画面（R13 の形） */
  paperTitle: "{schedule} PDF",
  save: "PDF 保存",
  print: "印刷",
  loading: "PDF 作成中…",
  /* 紙の中の文 */
  titleGroup: "{schedule}",
  titleSite: "{schedule}（{siteShort}別）",
  titleVendor: "{schedule}（{contractor}別）",
  created: "作成",
  staff: "担当",
  colName: "{task}",
  /** 概略の紙の1列目（見出し＝工種。#291） */
  colTrade: "{trade}",
  /** 概略の紙の右端（見出しの説明。#291） */
  colNote: "備考",
  colAssignee: "担当",
  colStart: "開始",
  colDays: "日数",
  colProgress: "進捗",
  /** ％ OFF の会社の列（#292。空／着手／✓） */
  colState: "状況",
  colCrew: "人",
  colAmount: "金額",
  internal: "社内用",
  colSite: "{siteShort}・{task}",
  colPerson: "{contractor}・担当",
  colSites: "{siteShort}",
  colOverlap: "重なり",
  crewDay: "その日の人数",
  /** 旬・月の目盛の下の段（#290。#291 で週は旬）：旬・月ごとの延べの人数 */
  crewSum: "延べ人数",
  today: "今日",
  legendDone: "済み",
  legendPlan: "予定",
  legendOff: "休日",
  legendStop: "中止",
  /** {siteShort}の休み（#293）：日の目盛の見出しの字・旬と月の目盛の帯 */
  siteOffMark: "休",
  siteOffBand: "休み",
  /** CSV の「中止の日」に{siteShort}の休みを入れるときの後ろ（#293） */
  siteOffSuffix: "（休み）",
  /** CSV の「日のメモ」（#299）：日付の見出しのメモ（{siteShort}のメモ）は1行目（見出しのすぐ下）の行に。種類の欄の名前 */
  dayNotesRow: "{siteShort}のメモ",
  /** 旬・月の目盛の凡例（#290。#291 で週は旬）：日曜・祝日は塗らず、会社の休みと中止を帯で */
  legendCompanyOff: "会社の休み",
  legendMilestone: "節目",
  legendToday: "今日の線",
  legendOverlap: "重なり",
  done100: "済",
  memoMark: "メモ",
  /** 下の注記：「休日（日曜・祝日）は日数に含みません。進捗は 9月30日 の時点です。」 */
  note: (off: string | null, progressAt: string | null, what = "進捗") =>
    [off ? `休日（${off}）は日数に含みません。` : "", progressAt ? `${what}は ${progressAt} の時点です。` : ""].join(""),
  page: (n: number, m: number) => `${n} / ${m}`,
  late: (n: number) => `${n}日遅れ`,
  sitesN: (n: number) => `${n}{siteShort}`,
  daysN: (n: number) => `${n}日`,
  /* CSV・Excel の2枚目（データ） */
  sheetMain: "{schedule}",
  sheetData: "データ",
  kindLabel: { task: "{task}", heading: "見出し", memo: "メモ", milestone: "節目" } as Record<ScheduleRow["kind"], string>,
  yes: "○",
} as const;

/**
 * CSV の見出し（案件の工程表。docs/39 §7.2 の8）。金額は入れたときだけ最後に。
 * 「前の{task}」は会社の言葉（scheduleFill で埋める）。「雨天も」の列は現場力だけ（営業力は scheduleCsvHead が外す。#142 段5 No.127）
 */
export const SCHEDULE_CSV_HEAD = ["種類", "見出し", "名前", "担当", "開始", "終わり", "日数", "進捗（%）", "状況", "着手日", "完了日", "人数", "休日も", "雨天も", "前の{task}", "中止の日", "休みの日", "作業の日", "{siteShort}の作業日", "日のメモ", "メモ"] as const;
/** 「雨天も」の列の位置（営業力は列ごと外す） */
export const SCHEDULE_CSV_RAIN_COL = SCHEDULE_CSV_HEAD.indexOf("雨天も");
/** CSV の見出しを会社の言葉で（rain＝scheduleShowsRain。false なら「雨天も」の列を外す） */
export const scheduleCsvHead = (fill: (text: string) => string, rain: boolean): string[] => SCHEDULE_CSV_HEAD.filter((_h, i) => rain || i !== SCHEDULE_CSV_RAIN_COL).map(fill);
/** CSV の見出し（全体の工程表）。{customer} は会社の言葉。「現場」は {siteShort}（#142 段5。fill で埋める） */
export const overviewCsvHead = (customer: string) => ["{siteShort}", customer, "担当", "種類", "名前", "開始", "終わり", "日数", "進捗（%）", "状況", "着手日", "完了日", "人数", "重なりの日"] as const;

/** 紙の宛名：{顧客}の正式名＋敬称（「なし」なら名前だけ）。「株式会社マッシュ 御中」 */
export const scheduleAddressee = (name: string, honorific: string): string => `${name} ${honorific}`.trim();

/** GET /groups/:id/schedule/export の問い合わせ（from・to を省けば全体） */
export const ScheduleExportQuery = z.object({
  format: ScheduleExportFormat,
  paper: SchedulePaperKey.default("a3l"),
  /** 目盛（#290。#291 で週は旬＝week も旬として受ける）。省けば日（前の URL と同じ紙）。PDF だけに効く */
  scale: ScheduleScale.default("day"),
  from: z.string().optional(),
  to: z.string().optional(),
  include: z.string().max(200).optional(),
  /** 「{task}の列を広く」（#300）：1 なら1列目を広く。省けば前と同じ紙 */
  wideName: z.enum(["0", "1"]).optional(),
});
export type ScheduleExportQuery = z.infer<typeof ScheduleExportQuery>;
/** GET /schedule/overview/export の問い合わせ（期間は要る。open＝開いている現場の id を「,」で） */
export const OverviewExportQuery = ScheduleExportQuery.extend({
  from: z.string(),
  to: z.string(),
  mode: z.enum(["site", "vendor"]).default("site"),
  q: z.string().max(100).default(""),
  open: z.string().max(40 * 300).default(""),
});
export type OverviewExportQuery = z.infer<typeof OverviewExportQuery>;
